import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface MarketsMobileProps {
  t: any;
  isRTL: boolean;
}

const MarketsSection = styled.section`
  padding: 4rem 1.5rem;
  background: white;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 100%;
  margin: 0 auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 2rem;
  color: #1f2937;
  margin-bottom: 1rem;
  position: relative;
  font-weight: 700;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 3px;
  }
`;

const SectionSubtitle = styled(motion.h3)<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #1f2937;
  font-weight: 400;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  margin-top: 1.5rem;
  line-height: 1.4;
`;

const MarketsGrid = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  margin-bottom: 3rem;
`;

const MarketCard = styled(motion.div)<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid rgba(220, 38, 38, 0.1);
  padding: 2rem 1.5rem;
  border-radius: 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const MarketIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  box-shadow: 0 10px 25px rgba(220, 38, 38, 0.3);
  
  img {
    width: 40px;
    height: 40px;
    filter: brightness(0) invert(1);
  }
`;

const MarketTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  line-height: 1.3;
`;

const MarketDescription = styled.p<{ $isRTL: boolean }>`
  color: #1f2937;
  line-height: 1.6;
  font-size: 0.95rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  margin-bottom: 1.5rem;
`;

const MarketFeatures = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  justify-content: center;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const FeatureTag = styled(motion.span)`
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.2) 0%, rgba(185, 28, 28, 0.2) 100%);
  border: 1px solid rgba(220, 38, 38, 0.3);
  color: #1f2937;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
`;

const AdvantagesSection = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid rgba(220, 38, 38, 0.1);
  padding: 2.5rem 1.5rem;
  border-radius: 20px;
  color: #1f2937;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const AdvantagesContent = styled.div`
  position: relative;
  z-index: 2;
`;

const AdvantagesTitle = styled(motion.h3)`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.8rem;
`;

const AdvantagesSubtitle = styled(motion.p)`
  font-size: 1rem;
  color: #1f2937;
  margin-bottom: 2rem;
`;

const AdvantagesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`;

const AdvantageItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-align: left;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(220, 38, 38, 0.1);
  padding: 1.2rem;
  border-radius: 15px;
  
  &:active {
    transform: scale(0.98);
  }
  
  .icon {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 12px;
    box-shadow: 0 6px 15px rgba(220, 38, 38, 0.3);
    
    img {
      width: 25px;
      height: 25px;
      filter: brightness(0) invert(1);
    }
  }
  
  .text {
    font-size: 0.95rem;
    line-height: 1.5;
    font-weight: 500;
    color: #1f2937;
  }
`;

const MarketsMobile: React.FC<MarketsMobileProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const markets = [
    {
      title: t.market2,
      description: t.market2Desc,
      icon: "/images/markets/Industrie.svg",
      features: t.marketFeatures.industry
    },
    {
      title: t.market3,
      description: t.market3Desc,
      icon: "/images/markets/Infrastructure.svg",
      features: t.marketFeatures.infrastructure
    },
    {
      title: t.market1,
      description: t.market1Desc,
      icon: "/images/markets/Bâtiment & Tertiaire.svg",
      features: t.marketFeatures.building
    }
  ];

  const advantages = [
    { 
      icon: "/images/avantages/Économies d'échelle à travers des prix compétitifs.svg", 
      text: t.advantages[0]
    },
    { 
      icon: "/images/avantages/Sécurité en termes de qualité d'approvisionnement.svg", 
      text: t.advantages[1]
    },
    { 
      icon: "/images/avantages/Savoir-faire particulier TECHYID.svg", 
      text: t.advantages[2]
    },
    { 
      icon: "/images/avantages/Partenariats avec des grands fabricants mondiaux.svg", 
      text: t.advantages[3]
    },
    { 
      icon: "/images/avantages/Mise en disponibilité des produits.svg", 
      text: t.advantages[4]
    },
    { 
      icon: "/images/avantages/Assistance technique gratuite.svg", 
      text: t.advantages[5]
    },
    { 
      icon: "/images/avantages/Assistance pour la soumission à des marchés.svg", 
      text: t.advantages[6]
    },
    { 
      icon: "/images/avantages/Échanges d'expériences.svg", 
      text: t.advantages[7]
    }
  ];

  return (
    <MarketsSection id="markets" ref={ref}>
      <Container $isRTL={isRTL}>
        <SectionHeader>
          <SectionTitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            {t.marketsTitle}
          </SectionTitle>
          <SectionSubtitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t.marketsSubtitle}
          </SectionSubtitle>
        </SectionHeader>

        <MarketsGrid $isRTL={isRTL}>
          {markets.map((market, index) => (
            <MarketCard
              key={index}
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.5, 
                delay: 0.3 + index * 0.15
              }}
            >
              <MarketIcon>
                <img src={market.icon} alt={market.title} />
              </MarketIcon>
              <MarketTitle $isRTL={isRTL}>{market.title}</MarketTitle>
              <MarketDescription $isRTL={isRTL}>{market.description}</MarketDescription>
              <MarketFeatures $isRTL={isRTL}>
                {market.features.map((feature: string, featureIndex: number) => (
                  <FeatureTag key={featureIndex}>
                    {feature}
                  </FeatureTag>
                ))}
              </MarketFeatures>
            </MarketCard>
          ))}
        </MarketsGrid>

        <AdvantagesSection>
          <AdvantagesContent>
            <AdvantagesTitle
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              {t.advantagesTitle}
            </AdvantagesTitle>
            <AdvantagesSubtitle
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
{t.advantagesSubtitle}
            </AdvantagesSubtitle>
            <AdvantagesGrid>
              {advantages.map((advantage: any, index: number) => (
                <AdvantageItem
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ 
                    duration: 0.4, 
                    delay: 1 + index * 0.05
                  }}
                >
                  <div className="icon">
                    <img src={advantage.icon} alt={advantage.text} />
                  </div>
                  <div className="text">{advantage.text}</div>
                </AdvantageItem>
              ))}
            </AdvantagesGrid>
          </AdvantagesContent>
        </AdvantagesSection>
      </Container>
    </MarketsSection>
  );
};

export default MarketsMobile; 